import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';
import { AppDispatch } from 'store/store';
import { reportApi } from 'api';
import { ReportType } from 'modules/core/enums';
import { setIsGeneratingWorksheet } from 'store/reducers/loanVariablesDialog.reducer';
import { getSubmitVehicleObjectQuery } from 'modules/core/utils';
import { RACAffordabilityEngineService } from 'modules/RACAffordabilityEngine/RACAffordabilityEngine.service';
import { RAC_FORM_FOR_LOAN_VARIABLES_NAMES_MAPPER } from 'modules/core/constants/mappers/submitVehicles.mapper';
import { ILenderVehicle, IRACVehicle } from 'modules/core/types';
import { IDealSourceData } from 'modules/core/types/dealSource';
import { IResidualValues } from 'modules/core/types/balloon';
import { getVendorName } from 'store/selectors/ui.selector';
import { useFormContext } from 'react-hook-form';
import { useFormOverrideLtv } from 'hooks/useFormOverrideLtv';

import { ICreateUserVehicle } from '../../AddVehicleDialog/types';
import { matchLoanTerms } from '../balloonPayment/BalloonPayment.service';

interface IUseHandleViewWorksheetProps {
  modalId: string;
  currentVehicleData: IRACVehicle | ILenderVehicle | undefined;
  loanTermsList: number[];
  isDealSourceVehicle: boolean;
  isAddedUserVehicle: boolean;
  createdUserVehicle: ICreateUserVehicle;
  dealSourceData: IDealSourceData;
  ltvTaxesFeesFilter: any;
  balloonResidualValues: IResidualValues | null;
  balloonTerms: any[];
  isShopByCallbackMode: boolean;
  getRequestParams: () => any;
  getValues: () => any;
  getAprForLoanVariablesRequest: () => any;
  getOverrideAprForLoanVariablesRequest: () => number | undefined;
}

export const useHandleViewWorksheet = ({
  modalId,
  currentVehicleData,
  loanTermsList,
  isDealSourceVehicle,
  isAddedUserVehicle,
  createdUserVehicle,
  dealSourceData,
  ltvTaxesFeesFilter,
  balloonResidualValues,
  balloonTerms,
  isShopByCallbackMode,
  getRequestParams,
  getValues,
  getAprForLoanVariablesRequest,
  getOverrideAprForLoanVariablesRequest,
}: IUseHandleViewWorksheetProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const vendorName = useSelector(getVendorName);
  const { getValues: commonGetValues } = useFormContext();
  const { getLtvHighLimit } = useFormOverrideLtv(commonGetValues);

  const handleViewWorksheet = useCallback(async () => {
    dispatch(setIsGeneratingWorksheet({ modalId, isGeneratingWorksheet: true }));

    try {
      const requestParams = getRequestParams();
      const values = commonGetValues();

      const vehicleParameters = getSubmitVehicleObjectQuery(
        { ...currentVehicleData, ...values, vendorName: vendorName, isLender: true },
        RACAffordabilityEngineService.getSubmitQueryForLoanVariables,
        RAC_FORM_FOR_LOAN_VARIABLES_NAMES_MAPPER,
      );

      const { balloonToggle: isBalloonToggled, annualMileage, balloonPayment, taxes } = getValues().loanVariablesForm;
      const balloon_residual_values = isBalloonToggled
        ? balloonResidualValues?.perAnnualMileage?.[annualMileage?.value]?.perTerm
        : undefined;

      const actualLoanTerms = isBalloonToggled ? matchLoanTerms(loanTermsList, balloonTerms) : loanTermsList;

      const worksheetData = {
        ...vehicleParameters,
        ...requestParams,
        taxes: taxes || 0,
        vehicle_id: currentVehicleData?.id,
        guidebook_value: undefined,
        estimated_fees: undefined,
        year: currentVehicleData?.year,
        ...(isDealSourceVehicle && {
          vehicle_id: dealSourceData.vehicle_id,
          year: parseInt(dealSourceData.sale_vehicle.year),
          manually_created: dealSourceData.manually_created,
        }),
        ...(isAddedUserVehicle && {
          vehicle_id: createdUserVehicle.id,
          year: createdUserVehicle.year,
          manually_created: true,
        }),
        apr: getAprForLoanVariablesRequest(),
        override_apr: getOverrideAprForLoanVariablesRequest(),
        override_vsc: dealSourceData?.finance_details?.override_vsc,
        total_ltv_limit: dealSourceData?.finance_details?.total_ltv_limit,
        bank_discount: values.feesAndTaxes.bankDiscount,
        bank_fees: values.feesAndTaxes.bankFees,
        lender_name: values.lenderName,
        is_ltv: ltvTaxesFeesFilter,
        deal_source_ltv_high_limit: dealSourceData?.finance_details?.high_ltv_limit,
        ltv_high_limit: getLtvHighLimit({
          isShopByCallbackMode,
          sbcLtv: (values as any).shopByCallback?.sbcLtv,
          tierLtvLimit: (values as any).tier?.ltv_limit,
          dealSourceLtv: dealSourceData?.finance_details?.high_ltv_limit,
        }),
        monthly_income: values.grossMonthlyIncome,
        targeted_payment: requestParams.approved_monthly_payment,
        approved_monthly_payment: requestParams.approved_monthly_payment,
        loan_terms: actualLoanTerms,
        ...(isBalloonToggled && {
          balloon_residual_values,
          balloon_terms: balloonTerms,
          balloon_payment: balloonPayment,
        }),
      } as any;

      const { data: reportBlob } = await reportApi.postPrintReport(worksheetData, ReportType.WORKSHEET);
      const fileURL = URL.createObjectURL(reportBlob);

      window.open(fileURL);
    } catch (error) {
      Sentry.captureException(error);
      alert('An error occurred while generating the worksheet. Please try again later.');
    } finally {
      dispatch(setIsGeneratingWorksheet({ modalId, isGeneratingWorksheet: false }));
    }
  }, [
    getRequestParams,
    currentVehicleData,
    dispatch,
    modalId,
    commonGetValues,
    vendorName,
    isAddedUserVehicle,
    createdUserVehicle,
    ltvTaxesFeesFilter,
    isDealSourceVehicle,
    dealSourceData,
    loanTermsList,
    balloonResidualValues,
    balloonTerms,
    getValues,
    isShopByCallbackMode,
    getAprForLoanVariablesRequest,
    getOverrideAprForLoanVariablesRequest,
    getLtvHighLimit,
  ]);

  return handleViewWorksheet;
};
